import { useEffect, useState } from 'react';
import { useRealtime } from 'src/modules/shared/realtime/hooks/provider-bounded/use-realtime';
import { isCommunicationsRealtimeEnabled } from 'src/modules/shared/realtime/communications/utils/release-gate.util';
import type {
  ConversationRealtimeInterruption,
  ConversationRealtimeNoticeState,
  UseConversationRealtimeNoticeParams,
} from '../../types/realtime/conversation-realtime-notice.types';

/**
 * Delays interruption feedback without changing subscription or recovery behavior.
 * @param params - Existing conversation and funnel topic identities.
 * @returns A quiet healthy/disabled state, or the localized component's notice state.
 */
export const useConversationRealtimeNotice = ({
  funnelId,
  conversationId,
}: UseConversationRealtimeNoticeParams): ConversationRealtimeNoticeState => {
  const { status, topicStatuses } = useRealtime();
  const topics = [
    ...(funnelId ? [`communications.funnel.${funnelId}`] : []),
    ...(conversationId
      ? [`communications.conversation.${conversationId}`]
      : []),
  ];
  const scope = `${funnelId}:${conversationId}`;
  const interrupted =
    isCommunicationsRealtimeEnabled() &&
    status !== 'disabled' &&
    topics.length > 0 &&
    (status !== 'connected' ||
      topics.some((topic) => topicStatuses[topic] !== 'subscribed'));
  const unavailable =
    (status !== 'connected' &&
      status !== 'connecting' &&
      status !== 'reconnecting') ||
    topics.some(
      (topic) =>
        topicStatuses[topic] === 'error' ||
        topicStatuses[topic] === 'unavailable'
    );
  const [progress, setProgress] =
    useState<ConversationRealtimeInterruption | null>(null);

  useEffect(() => {
    setProgress(null);
    if (!interrupted) return;
    const brief = window.setTimeout(
      () => setProgress({ scope, phase: 'brief' }),
      3000
    );
    const lasting = window.setTimeout(
      () => setProgress({ scope, phase: 'lasting' }),
      15000
    );
    return () => {
      window.clearTimeout(brief);
      window.clearTimeout(lasting);
    };
  }, [interrupted, scope]);

  if (!interrupted || progress?.scope !== scope) return null;
  return unavailable || progress.phase === 'lasting'
    ? 'delayed'
    : 'reconnecting';
};
